import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme } from "@mui/material";

const themes = {
  dark: {
    vars: {
      "--output-bg": "#111417",
      "--output-text": "#00e676",
    },
  },
  github: {
    vars: {
      "--output-bg": "#ffffff",
      "--output-text": "#0969da",
    },
  },
  dracula: {
    vars: {
      "--output-bg": "#1e1f29",
      "--output-text": "#50fa7b",
    },
  },
  monokai: {
    vars: {
      "--output-bg": "#33352e",
      "--output-text": "#a6e22e",
    },
  },
};

export default function RubyCodeEditorOutput({
  code,
  opal,
  loading = false,
  themeName: externalTheme
}) {
  const [output, setOutput] = useState([]);
  const muiTheme = useTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";
  const themeName = externalTheme !== undefined ? externalTheme : (isDarkMode ? "dark" : "github");

  useEffect(() => {
    const vars = themes[themeName]?.vars || (isDarkMode ? themes.dark.vars : themes.github.vars);
    Object.entries(vars).forEach(([key, val]) =>
      document.documentElement.style.setProperty(key, val)
    );
  }, [themeName, isDarkMode]);

  const { vars } = themes[themeName] || (isDarkMode ? themes.dark : themes.github);
  const outputBg = vars["--output-bg"];
  const outputText = vars["--output-text"];

  useEffect(() => {
    if (!code || !code.trim() || !opal) {
      setOutput([]);
      return;
    }

    const runRuby = async () => {
      try {
        const logs = [];

        // Capture output using Opal
        if (window.Opal && opal) {
          // Store original console methods
          const originalLog = console.log;
          const originalError = console.error;

          // Override console methods to capture output
          console.log = (...args) => {
            logs.push(args.map(arg => String(arg)).join(' '));
          };
          console.error = (...args) => {
            logs.push('❌ ' + args.map(arg => String(arg)).join(' '));
          };

          try {
            // Compile and execute Ruby code
            const compiled = opal.compile(code);
            if (compiled) {
              // Execute the compiled JavaScript
              // eslint-disable-next-line
              eval(compiled);
            }
          } catch (compileErr) {
            logs.push(`❌ Compilation error: ${compileErr.message}`);
          } finally {
            // Restore original console methods
            console.log = originalLog;
            console.error = originalError;
          }
        } else {
          throw new Error("Opal runtime not loaded");
        }

        setOutput(logs.length > 0 ? logs : ["(No output)"]);
      } catch (err) {
        setOutput([`❌ ${err.message}`]);
      }
    };

    runRuby();
  }, [code, opal]);

  // Format output with line numbers
  const formatOutput = (outputLines) => {
    if (!outputLines || outputLines.length === 0) return null;

    return outputLines.map((line, index) => {
      const lineNumber = index + 1;
      return (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            fontFamily: "'Fira Code', monospace",
            fontSize: "0.9rem",
            lineHeight: 1.6,
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              color: isDarkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
              minWidth: "40px",
              textAlign: "right",
              paddingRight: 1,
              userSelect: "none",
              fontSize: "0.85rem",
            }}
          >
            {lineNumber}
          </Box>
          <Box
            sx={{
              flex: 1,
              color: outputText,
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
            }}
          >
            {line}
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 2,
        p: 1.5,
        background: outputBg,
        color: outputText,
        fontFamily: "'Fira Code', monospace",
        fontSize: "0.9rem",
        whiteSpace: "pre-wrap",
        overflowY: "auto",
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : muiTheme.palette.divider
          }`,
        transition: "background 0.4s ease, color 0.3s ease, border-color 0.3s ease",
      }}
    >
      {loading ? (
        <Typography sx={{ color: outputText }}>⏳ Running Ruby...</Typography>
      ) : output.length > 0 ? (
        formatOutput(output)
      ) : (
        <Typography sx={{ color: muiTheme.palette.text.secondary }}>
          💡 Output will appear here after execution
        </Typography>
      )}
    </Box>
  );
}

